import MemberApi from '@api/MemberApi'
import { MEMBER_UPDATE_SUCCESSFUL } from '@api/Statuses'

import formattable from '@mixins/formattable'
import ClassPlanFromSubGroupCkData from '@mixins/ClassPlanFromSubGroupCkData'

import { mapFields } from 'vuex-map-fields'
import { locatedInUStoForeignIndicator, convertAddressType, errToString } from '@utils'

export default {
  mixins: [
    formattable,
    ClassPlanFromSubGroupCkData
  ],
  computed: {
    isDependent() {
      return this.memberDetails && this.memberDetails.member.memberType === 'Dependent'
    },
    canEditHomeAddress () {
      return !this.isDependent
    },
    allFormsReady() {
      let ready = true
      Object.values(this.ready).map(formReady => {
        if (formReady != true) {
          ready = false
        }
      })
      return ready
    },
    ...mapFields([
      'memberManagement.memberDetails.member.subgroupCk', // todo subgroupCk -> subGroupCk?
      'memberManagement.memberDetails.member.firstName',
      'memberManagement.memberDetails.member.middleInitial',
      'memberManagement.memberDetails.member.lastName',
      'memberManagement.memberDetails.member.suffix',
      'memberManagement.memberDetails.member.gender',
      'memberManagement.memberDetails.member.dateOfBirth',
      'memberManagement.memberDetails.member.ssn',
      'memberManagement.memberDetails.member.relationshipCode',

      'memberManagement.memberDetails.member.homeAddress',
      'memberManagement.memberDetails.member.clientMemberId',
      'memberManagement.memberDetails.member.clientHealthId',

      'memberManagement.memberDetails.member.divisionCode',
      'memberManagement.memberDetails.member.benefitOption',
      'memberManagement.memberDetails.member.location',
      'memberManagement.memberDetails.member.companyCode',
      'memberManagement.memberDetails.member.clientGroupNbr',
      'memberManagement.memberDetails.member.reportingCodes',

      'memberManagement.memberDetails.member.privacy',
      'memberManagement.memberDetails.member.privacyInfo',

      'memberManagement.memberDetails.member.additionalMemberInfo.writtenLanguage',
      'memberManagement.memberDetails.member.additionalMemberInfo.spokenLanguage',
      'memberManagement.memberDetails.member.additionalMemberInfo.raceCode',
      'memberManagement.memberDetails.member.additionalMemberInfo.ethnicityCode'
    ]),
    computedPersonalInformation: {
      get() {
        if (!this.memberDetails) {
          return {}
        }

        return {
          firstName: this.firstName,
          middleInitial: this.middleInitial,
          lastName: this.lastName,
          suffix: this.suffix,
          gender: this.gender,
          dob: this.dateOfBirth,
          ssn: this.ssn,
          clientHealthId: this.clientHealthId,
          relationship: this.relationshipCode,
          fileBoundId: this.fileBoundId
        }
      },
      set(newValue) {
        this.firstName = newValue.firstName
        this.middleInitial = newValue.middleInitial
        this.lastName = newValue.lastName
        this.suffix = newValue.suffix
        this.gender = newValue.gender
        this.dateOfBirth = newValue.dob
        this.ssn = newValue.ssn
        this.clientHealthId = newValue.clientHealthId
        this.relationshipCode = newValue.relationship
        this.fileBoundId = newValue.fileBoundId
      }
    },
    computedContactInformation: {
      get() {
        if (this.memberDetailsLoading) return {}
        else return this.memberDetails.member.homeAddress
      },
      set(newValue) {
        this.homeAddress = newValue
      }
    },
    computedReportingCodes: {
      get() {
        if (!this.memberDetails) {
          return {}
        }

        return {
          divisionCode: this.divisionCode,
          benefitOption: this.benefitOption,
          locationCode: this.location,
          companyCode: this.companyCode,
          clientGroupNumber: this.clientGroupNbr,
          reportingCode1: this.reportingCodes ? this.reportingCodes[0] : '',
          reportingCode2: this.reportingCodes ? this.reportingCodes[1] : '',
          reportingCode3: this.reportingCodes ? this.reportingCodes[2] : '',
          reportingCode4: this.reportingCodes ? this.reportingCodes[3] : '',
          reportingCode5: this.reportingCodes ? this.reportingCodes[4] : ''
        }
      },
      set(newValue) {
        this.divisionCode = newValue.divisionCode
        this.benefitOption = newValue.benefitOption
        this.location = newValue.locationCode
        this.companyCode = newValue.companyCode
        this.clientGroupNbr = newValue.clientGroupNumber
        this.reportingCodes = [
          newValue.reportingCode1,
          newValue.reportingCode2,
          newValue.reportingCode3,
          newValue.reportingCode4,
          newValue.reportingCode5
        ]
      }
    },
    computedLanguageAssistance: {
      get() {
        if (!this.memberDetails) {
          return {}
        }

        return {
          writtenLanguage: this.writtenLanguage,
          spokenLanguage: this.spokenLanguage,
          race: this.raceCode,
          ethnicity: this.ethnicityCode
        }
      },
      set(newValue) {
        this.writtenLanguage = newValue.writtenLanguage
        this.spokenLanguage = newValue.spokenLanguage
        this.raceCode = newValue.race
        this.ethnicityCode = newValue.ethnicity
      }
    }
  },
  data() {
    return {
      fileBoundId: '',
      subGroupCk: '',
      isSubmitting: false
    }
  },
  methods: {
    getMemberUpdatePayload(type) {
      return {
        fileBoundId: this.fileBoundId,
        transactionId: '',
        transactionType: type,
        memberType: this.memberDetails.member.memberType,
        subscriberCk: this.memberDetails.member.subscriberCk,
        clientHealthId:
          this.clientMemberId || this.memberDetails.member.clientHealthId,
        eyemedMemberId: this.eyemedId,
        memberFirstName: this.firstName,
        memberLastName: this.lastName,
        memberMidInitial: this.middleInitial,
        memberNameSuffix: this.suffix,
        memberDateOfBirth: this.toIso8601Date(
          this.dateOfBirth || this.memberDetails.member.dateOfBirth
        ),
        ssn: this.ssn,
        relationship: this.memberDetails.member.relationshipCode,
        gender: this.gender,
        phone: this.homeAddress.phone,
        email: this.homeAddress.email,
        memberIdSource: this.computedCurrentSubGroupPlanInfo.memberIdSource,
        primaryAddress: {
          line1: this.homeAddress.line1,
          line2: this.homeAddress.line2,
          line3: this.homeAddress.line3,
          city: this.homeAddress.city,
          state: this.homeAddress.state,
          zip: this.homeAddress.zip,
          county: this.homeAddress.county,
          type: convertAddressType(this.homeAddress),
          foreignIndicator: locatedInUStoForeignIndicator(
            this.homeAddress.locatedInUS,
            this.homeAddress.foreignIndicator
          )
        },
        coverage: {
          groupCk: this.memberDetails.member.groupCk,
          subGroupCk:
            this.memberDetails.member.subgroupCk ||
            this.memberDetails.member.subGroupCk,
          classId: this.memberDetails.member.classId,
          planId: this.memberDetails.member.planId,
          startDate: '', // todo from where
          endDate: '' // todo from where
        },
        reportingCodes: {
          divisionCode: this.computedReportingCodes.divisionCode,
          benefitOption: this.computedReportingCodes.benefitOption,
          locationGroupNumber: this.computedReportingCodes.locationCode,
          companyCode: this.computedReportingCodes.companyCode,
          clientGroupNumber: this.computedReportingCodes.clientGroupNumber,
          reportingCodes: this.reportingCodes || ['', '', '', '', '']
        },
        privacy: {
          firstName: this.computedPrivacyAddress.firstName,
          midInitial: this.computedPrivacyAddress.middleInitial || this.computedPrivacyAddress.midInitial,
          lastName: this.computedPrivacyAddress.lastName,
          address: {
            line1: this.computedPrivacyAddress.line1,
            line2: this.computedPrivacyAddress.line2,
            line3: this.computedPrivacyAddress.line3,
            city: this.computedPrivacyAddress.city,
            state: this.computedPrivacyAddress.state,
            zip: this.computedPrivacyAddress.zipCode,
            county: this.computedPrivacyAddress.county,
            foreignIndicator: locatedInUStoForeignIndicator(this.computedPrivacyAddress.locatedInUS, this.computedPrivacyAddress.foreignIndicator)
          }
        },
        ethnicity: {
          code: this.ethnicityCode,
          description: ''
        },
        race: {
          code: this.raceCode,
          description: ''
        },
        spokenLanguage: {
          code: this.spokenLanguage,
          description: ''
        },
        writtenLanguage: {
          code: this.writtenLanguage,
          description: ''
        }
      }
    },
    updateMember(type) {
      let body = this.getMemberUpdatePayload(type)
      let memberCk = this.memberDetails.member.memberCk

      return new MemberApi({
        simulateExample: 'Update Subscriber'
      }).updateMember({
        ...body,
        memberCk
      })
    },
    submit() {
      this.isSubmitting = true

      this.updateMember('Update')
        .then(res => {
          if (res.status !== MEMBER_UPDATE_SUCCESSFUL) {
            console.warn('Member update not successful', res.status)
            // TODO: handle not successful
            return
          }

          const afterPush = () => this.$store.commit('SET_ANNOUNCEMENT', 'Member updated')
          this.$router.push(
            {
              name: 'member-details',
              params: { memberCk: this.memberDetails.member.memberCk }
            },
            afterPush,
            afterPush
          )
        })
        .catch(err => {
          errToString(err)
            .then((errStr) => {
              console.warn(errStr)
            })
        })
        .finally(() => {
          this.isSubmitting = false
        })
    }
  },
  watch: {
    'memberDetails.member.subgroupCk': function(subGroupCk) {
      // we'd normally just do a mapState for memberDetails.member.subgroupCk, but the g returned in subgroupCk in the API isn't capitalized
      this.subGroupCk = subGroupCk
    },
    'memberDetails.member.subscriberCk'(subscriberCk) {
      this.$store.dispatch('GET_MEMBER_ADDRESSES', { memberCk: subscriberCk })
    }
  }
}
